<script setup lang="ts">
/**
 * PublicLayout.vue — anonymous chrome for portal routes.
 *
 * No sidebar, no topbar, no user menu, no MSAL interaction.
 * Mirrors the portal-header structure from portal.html exactly.
 *
 * HOA name is passed as a prop (loaded by PortalSubmissionView from GET meta).
 * When not yet loaded, a blank header renders (no flash of wrong content).
 */
import { RouterView } from 'vue-router';

interface Props {
  hoaName?: string;
}

withDefaults(defineProps<Props>(), {
  hoaName: '',
});
</script>

<template>
  <div class="min-h-screen" style="background: linear-gradient(180deg, var(--color-navy-50), var(--color-bg, #f8f9fb) 18%)">
    <!-- Portal header — navy, no sidebar -->
    <header class="bg-navy-900 text-white py-[22px]" data-testid="portal-header">
      <div class="max-w-[900px] mx-auto px-6 flex gap-4 items-center">
        <!-- Brand mark -->
        <div class="w-[42px] h-[42px] flex-shrink-0">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" class="w-full h-full">
            <path d="M3 11.5L12 4l9 7.5" />
            <path d="M5 10v10h14V10" />
            <circle cx="12" cy="14" r="2.2" fill="currentColor" stroke="none" />
          </svg>
        </div>
        <!-- HOA name -->
        <div>
          <h1 class="text-[17px] font-bold m-0 tracking-[-0.01em]" data-testid="portal-hoa-name">
            {{ hoaName || 'HOA Watch' }}
          </h1>
          <div class="text-[12.5px] mt-[1px]" style="color: color-mix(in oklch, white, var(--color-navy-100, #e8ecf4) 50%)">
            Community issue submission · powered by HOA Watch
          </div>
        </div>
        <!-- Nav links — hidden at ≤640px per prototype -->
        <nav class="ml-auto hidden sm:flex gap-[18px] items-center text-[13.5px]" style="color: color-mix(in oklch, white, var(--color-navy-100, #e8ecf4) 50%)" data-testid="portal-nav">
          <a href="#" class="hover:text-white transition-colors">About the board</a>
          <a href="#" class="hover:text-white transition-colors">Community guidelines</a>
          <a href="/login" class="text-white font-semibold hover:opacity-80 transition-opacity">Sign in &rarr;</a>
        </nav>
      </div>
    </header>

    <!-- Page content -->
    <main>
      <RouterView />
    </main>
  </div>
</template>
